import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { cn } from '@/lib/utils';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  className?: string;
}

/**
 * Visor de imagen SIMPLE — solo para ver la factura, no para analizarla.
 *
 * A diferencia de `<ImageViewer>` (que tiene barra de herramientas con
 * brillo/contraste/rotación/inversión y se usa en el panel del contador),
 * este visor del tenant NO tiene chrome: la imagen entra fit-to-container y
 * se puede acercar con scroll o doble-click + arrastrar para desplazar.
 * Nada de edición.
 */
export function ImageLightbox({ src, alt, className }: ImageLightboxProps) {
  const [zoom, setZoom] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  // Reset al cambiar de imagen.
  useEffect(() => {
    setZoom(1);
    setTx(0);
    setTy(0);
  }, [src]);

  function onWheel(e: ReactWheelEvent<HTMLDivElement>) {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => {
      const next = Math.min(5, Math.max(1, z * delta));
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return next;
    });
  }

  function toggleZoom() {
    setZoom((z) => (z > 1 ? 1 : 2));
    setTx(0);
    setTy(0);
  }

  function onMouseDown(e: ReactMouseEvent<HTMLDivElement>) {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, tx, ty };
  }
  function onMouseMove(e: ReactMouseEvent<HTMLDivElement>) {
    if (!dragging) return;
    setTx(dragStart.current.tx + (e.clientX - dragStart.current.x));
    setTy(dragStart.current.ty + (e.clientY - dragStart.current.y));
  }
  function onMouseUp() {
    setDragging(false);
  }

  return (
    <div
      className={cn(
        'relative flex h-full w-full items-center justify-center overflow-hidden bg-muted select-none',
        className
      )}
      onWheel={onWheel}
      onDoubleClick={toggleZoom}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="max-h-full max-w-full object-contain pointer-events-none"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${zoom})`,
          transition: dragging ? 'none' : 'transform 80ms ease-out',
        }}
      />
    </div>
  );
}
